import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Plus, FileText, Download, CheckCircle, XCircle,
  X, Loader2, Shield, Eye, ChevronDown, Filter, Ban, Mail, Send, ExternalLink, Copy
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import ShopLayout from '@/components/shop/ShopLayout';

const TAX_RATES = [
  { value: 4, label: '4% (Superreducido)' },
  { value: 10, label: '10% (Reducido)' },
  { value: 21, label: '21% (General)' },
];

const PAYMENT_METHODS = ['bank_transfer', 'cash', 'card', 'other'];

const emptyItem = { description: '', quantity: 1, price: '', tax_rate: 21 };

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-600',
  issued: 'bg-blue-100 text-blue-700',
  paid: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-600',
};

const ShopInvoicesPage = () => {
  const { api } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [shop, setShop] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailInput, setEmailInput] = useState('');

  // Create form
  const [customer, setCustomer] = useState({ name: '', nif: '', address: '', city: '', postal_code: '', email: '' });
  const [items, setItems] = useState([{ ...emptyItem }]);
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const shopsRes = await api.get('/shops');
      if (shopsRes.data.length === 0) { navigate('/shop/setup'); return; }
      const s = shopsRes.data[0];
      setShop(s);
      const invRes = await api.get(`/shops/${s.id}/invoices`);
      setInvoices(invRes.data);
    } catch (e) { console.error('Failed to load invoices:', e); }
    finally { setLoading(false); }
  };

  const filtered = useMemo(() => invoices.filter(inv => {
    if (statusFilter !== 'all' && inv.status !== statusFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return inv.invoice_number.toLowerCase().includes(q) ||
        inv.customer?.name?.toLowerCase().includes(q) ||
        inv.customer?.nif?.toLowerCase().includes(q);
    }
    return true;
  }), [invoices, statusFilter, searchQuery]);

  const calcTotals = () => {
    let subtotal = 0;
    let taxTotal = 0;
    items.forEach(item => {
      const line = (parseFloat(item.price) || 0) * (parseInt(item.quantity) || 0);
      subtotal += line;
      taxTotal += line * (item.tax_rate / 100);
    });
    return { subtotal, taxTotal, total: subtotal + taxTotal };
  };

  const resetForm = () => {
    setCustomer({ name: '', nif: '', address: '', city: '', postal_code: '', email: '' });
    setItems([{ ...emptyItem }]);
    setPaymentMethod('bank_transfer');
    setDueDate('');
    setNotes('');
  };

  const handleCreate = async () => {
    if (!customer.name || items.length === 0 || !items[0].description || !items[0].price) return;
    setSaving(true);
    try {
      await api.post(`/shops/${shop.id}/invoices`, {
        customer,
        items: items.map(i => ({ ...i, price: parseFloat(i.price), quantity: parseInt(i.quantity) })),
        payment_method: paymentMethod,
        due_date: dueDate || null,
        notes: notes || null,
      });
      setShowCreate(false);
      resetForm();
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Error creating invoice'); }
    finally { setSaving(false); }
  };

  const handleMarkPaid = async (invId) => {
    try {
      await api.post(`/shops/${shop.id}/invoices/${invId}/mark-paid`);
      fetchData();
      setShowDetail(null);
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const handleCancel = async (invId) => {
    try {
      await api.post(`/shops/${shop.id}/invoices/${invId}/cancel`);
      fetchData();
      setShowDetail(null);
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const handleDownloadPDF = async (invId, invNumber) => {
    try {
      const res = await api.get(`/shops/${shop.id}/invoices/${invId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invNumber}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert('Error downloading PDF'); }
  };

  const handleSendEmail = async (invId) => {
    const email = emailInput || showDetail?.customer?.email;
    if (!email) { alert(t('inv_email_required')); return; }
    setSendingEmail(true);
    try {
      await api.post(`/shops/${shop.id}/invoices/${invId}/send-email`, {
        email,
        language: localStorage.getItem('vf_language') || 'es',
        portal_base_url: window.location.origin,
      });
      alert(t('inv_email_sent'));
      setEmailInput('');
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || t('inv_email_failed'));
    } finally { setSendingEmail(false); }
  };

  const addItem = () => setItems([...items, { ...emptyItem }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i, field, val) => {
    const copy = [...items];
    copy[i] = { ...copy[i], [field]: val };
    setItems(copy);
  };

  const totals = calcTotals();
  const statusLabel = (s) => ({ draft: t('inv_draft'), issued: t('inv_issued'), paid: t('inv_paid'), cancelled: t('inv_cancelled') }[s] || s);
  const payLabel = (p) => ({ bank_transfer: t('inv_bank'), cash: t('inv_cash'), card: t('inv_card'), other: t('inv_other') }[p] || p);

  if (loading) {
    return <ShopLayout><div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" /></div></ShopLayout>;
  }

  return (
    <ShopLayout>
      <div className="space-y-6" data-testid="invoices-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-[#1e3a5f]">{t('inv_title')}</h2>
            <p className="text-slate-500 text-sm">{invoices.length} {t('inv_title').toLowerCase()}</p>
          </div>
          <button onClick={() => { resetForm(); setShowCreate(true); }}
            className="vf-btn-primary flex items-center gap-2" data-testid="create-invoice-btn">
            <Plus className="w-5 h-5" /> {t('inv_create')}
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder={t('inv_search')}
              className="w-full pl-10 pr-4 py-2.5 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none"
              data-testid="invoices-search" />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {['all', 'draft', 'issued', 'paid', 'cancelled'].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                className={`px-4 py-2 rounded-sm whitespace-nowrap text-sm ${statusFilter === s ? 'bg-[#1e3a5f] text-white' : 'bg-white border border-slate-200 text-slate-600'}`}>
                {s === 'all' ? t('inv_all') : statusLabel(s)}
              </button>
            ))}
          </div>
        </div>

        {/* Invoice List */}
        {filtered.length === 0 ? (
          <div className="text-center py-16 bg-white border-2 border-slate-100">
            <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500 text-lg">{t('inv_no_invoices')}</p>
            <p className="text-slate-400 text-sm mb-6">{t('inv_create_first')}</p>
            <button onClick={() => { resetForm(); setShowCreate(true); }} className="vf-btn-primary inline-flex items-center gap-2">
              <Plus className="w-5 h-5" /> {t('inv_create')}
            </button>
          </div>
        ) : (
          <div className="bg-white border-2 border-slate-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="invoices-table">
                <thead>
                  <tr className="border-b-2 border-slate-100 text-left">
                    <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('inv_number')}</th>
                    <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('inv_date')}</th>
                    <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('inv_customer')}</th>
                    <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('inv_total')}</th>
                    <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('inv_status')}</th>
                    <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('inv_verifactu')}</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(inv => (
                    <tr key={inv.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer"
                      onClick={() => setShowDetail(inv)} data-testid={`invoice-row-${inv.id}`}>
                      <td className="px-4 py-3 font-mono font-semibold text-[#1e3a5f]">{inv.invoice_number}</td>
                      <td className="px-4 py-3 text-slate-600">{inv.issue_date}</td>
                      <td className="px-4 py-3">
                        <div><p className="font-medium text-[#1e3a5f]">{inv.customer?.name}</p>
                          {inv.customer?.nif && <p className="text-xs text-slate-400">{inv.customer.nif}</p>}</div>
                      </td>
                      <td className="px-4 py-3 font-semibold text-[#22c55e]">{'\u20AC'}{inv.total.toFixed(2)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-sm text-xs font-medium ${STATUS_COLORS[inv.status]}`}>
                          {statusLabel(inv.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <Shield className="w-4 h-4 text-[#22c55e]" />
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={(e) => { e.stopPropagation(); handleDownloadPDF(inv.id, inv.invoice_number); }}
                          className="p-2 hover:bg-slate-100 rounded-sm text-slate-500 hover:text-[#1e3a5f]"
                          data-testid={`download-pdf-${inv.id}`}>
                          <Download className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Create Invoice Modal */}
      <AnimatePresence>
        {showCreate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50" onClick={() => setShowCreate(false)} />
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-[95vw] md:w-[700px] max-h-[90vh] bg-white z-10 flex flex-col overflow-hidden rounded-lg shadow-xl"
              data-testid="create-invoice-modal">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-xl font-bold text-[#1e3a5f]">{t('inv_create')}</h3>
                <button onClick={() => setShowCreate(false)} className="p-2 hover:bg-slate-100 rounded-sm"><X className="w-5 h-5" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-5">
                {/* Customer */}
                <div>
                  <h4 className="text-sm font-semibold text-[#1e3a5f] mb-2">{t('inv_customer')}</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <input type="text" placeholder={t('inv_customer_name') + ' *'} value={customer.name}
                      onChange={e => setCustomer(c => ({ ...c, name: e.target.value }))}
                      className="col-span-2 px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none"
                      data-testid="inv-customer-name" />
                    <input type="text" placeholder={t('inv_customer_nif')} value={customer.nif}
                      onChange={e => setCustomer(c => ({ ...c, nif: e.target.value }))}
                      className="px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none" />
                    <input type="text" placeholder={t('inv_customer_email')} value={customer.email}
                      onChange={e => setCustomer(c => ({ ...c, email: e.target.value }))}
                      className="px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none" />
                    <input type="text" placeholder={t('inv_customer_address')} value={customer.address}
                      onChange={e => setCustomer(c => ({ ...c, address: e.target.value }))}
                      className="px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none" />
                    <div className="flex gap-2">
                      <input type="text" placeholder={t('inv_customer_city')} value={customer.city}
                        onChange={e => setCustomer(c => ({ ...c, city: e.target.value }))}
                        className="flex-1 px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none" />
                      <input type="text" placeholder={t('inv_customer_postal')} value={customer.postal_code}
                        onChange={e => setCustomer(c => ({ ...c, postal_code: e.target.value }))}
                        className="w-24 px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none" />
                    </div>
                  </div>
                </div>

                {/* Items */}
                <div>
                  <h4 className="text-sm font-semibold text-[#1e3a5f] mb-2">{t('inv_items')}</h4>
                  <div className="space-y-2">
                    {items.map((item, i) => (
                      <div key={i} className="flex gap-2 items-start">
                        <input type="text" placeholder={t('inv_description')} value={item.description}
                          onChange={e => updateItem(i, 'description', e.target.value)}
                          className="flex-1 px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none text-sm"
                          data-testid={`inv-item-desc-${i}`} />
                        <input type="number" placeholder={t('inv_quantity')} value={item.quantity}
                          onChange={e => updateItem(i, 'quantity', e.target.value)}
                          className="w-16 px-2 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none text-sm text-center" />
                        <input type="number" step="0.01" placeholder={t('inv_price')} value={item.price}
                          onChange={e => updateItem(i, 'price', e.target.value)}
                          className="w-24 px-2 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none text-sm"
                          data-testid={`inv-item-price-${i}`} />
                        <select value={item.tax_rate} onChange={e => updateItem(i, 'tax_rate', parseFloat(e.target.value))}
                          className="w-24 px-2 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none text-sm">
                          {TAX_RATES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                        {items.length > 1 && (
                          <button onClick={() => removeItem(i)} className="p-2 text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                        )}
                      </div>
                    ))}
                    <button onClick={addItem} className="text-sm text-[#22c55e] hover:underline font-medium"
                      data-testid="add-line-btn">+ {t('inv_add_line')}</button>
                  </div>
                </div>

                {/* Totals */}
                <div className="bg-slate-50 p-4 space-y-1 text-sm">
                  <div className="flex justify-between"><span>{t('inv_subtotal')}</span><span>{'\u20AC'}{totals.subtotal.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span>{t('inv_tax')}</span><span>{'\u20AC'}{totals.taxTotal.toFixed(2)}</span></div>
                  <div className="flex justify-between font-bold text-lg text-[#1e3a5f] pt-2 border-t border-slate-200">
                    <span>{t('inv_total')}</span><span>{'\u20AC'}{totals.total.toFixed(2)}</span>
                  </div>
                </div>

                {/* Payment & Notes */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('inv_payment')}</label>
                    <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none">
                      {PAYMENT_METHODS.map(m => <option key={m} value={m}>{payLabel(m)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('inv_due_date')}</label>
                    <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('inv_notes')}</label>
                  <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                    className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none resize-none" />
                </div>
              </div>
              <div className="p-4 border-t border-slate-200 flex gap-3">
                <button onClick={() => setShowCreate(false)} className="vf-btn-secondary flex-1">{t('products_cancel')}</button>
                <button onClick={handleCreate} disabled={saving || !customer.name || !items[0]?.description || !items[0]?.price}
                  className="vf-btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="save-invoice-btn">
                  {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
                  {t('inv_save')}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Invoice Detail Modal */}
      <AnimatePresence>
        {showDetail && (
          <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={() => setShowDetail(null)}>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50" />
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-[95vw] md:w-[600px] max-h-[85vh] bg-white rounded-lg shadow-xl flex flex-col z-10"
              data-testid="invoice-detail-modal"
              onClick={e => e.stopPropagation()}>
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h3 className="text-xl font-bold text-[#1e3a5f]">{showDetail.invoice_number}</h3>
                  <span className={`px-2 py-1 rounded-sm text-xs font-medium ${STATUS_COLORS[showDetail.status]}`}>
                    {statusLabel(showDetail.status)}
                  </span>
                </div>
                <button onClick={() => setShowDetail(null)} className="p-2 hover:bg-slate-100 rounded-sm"><X className="w-5 h-5" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-400 mb-1">{t('inv_customer')}</p>
                    <p className="font-medium text-[#1e3a5f]">{showDetail.customer?.name}</p>
                    {showDetail.customer?.nif && <p className="text-sm text-slate-500">{showDetail.customer.nif}</p>}
                    {showDetail.customer?.address && <p className="text-sm text-slate-500">{showDetail.customer.address}</p>}
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-400 mb-1">{t('inv_date')}</p>
                    <p className="font-medium text-[#1e3a5f]">{showDetail.issue_date}</p>
                    {showDetail.due_date && <p className="text-sm text-slate-500">{t('inv_due_date')}: {showDetail.due_date}</p>}
                  </div>
                </div>
                <table className="w-full text-sm border border-slate-200">
                  <thead><tr className="bg-slate-50">
                    <th className="px-3 py-2 text-left">{t('inv_description')}</th>
                    <th className="px-3 py-2 text-center">{t('inv_quantity')}</th>
                    <th className="px-3 py-2 text-right">{t('inv_price')}</th>
                    <th className="px-3 py-2 text-center">{t('inv_tax_rate')}</th>
                    <th className="px-3 py-2 text-right">{t('inv_line_total')}</th>
                  </tr></thead>
                  <tbody>
                    {showDetail.items?.map((item, i) => {
                      const lt = item.quantity * item.price * (1 + item.tax_rate / 100);
                      return (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="px-3 py-2">{item.description}</td>
                          <td className="px-3 py-2 text-center">{item.quantity}</td>
                          <td className="px-3 py-2 text-right">{'\u20AC'}{item.price.toFixed(2)}</td>
                          <td className="px-3 py-2 text-center">{item.tax_rate}%</td>
                          <td className="px-3 py-2 text-right font-medium">{'\u20AC'}{lt.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="bg-slate-50 p-3 space-y-1 text-sm">
                  <div className="flex justify-between"><span>{t('inv_subtotal')}</span><span>{'\u20AC'}{showDetail.subtotal?.toFixed(2)}</span></div>
                  {showDetail.tax_breakdown?.map((tb, i) => (
                    <div key={i} className="flex justify-between text-slate-500">
                      <span>{t('inv_tax')} {tb.rate}%</span><span>{'\u20AC'}{tb.amount.toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between font-bold text-lg text-[#1e3a5f] pt-2 border-t border-slate-200">
                    <span>{t('inv_total')}</span><span>{'\u20AC'}{showDetail.total?.toFixed(2)}</span>
                  </div>
                </div>
                {/* VeriFactu info */}
                <div className="bg-[#1e3a5f]/5 border border-[#1e3a5f]/20 p-3 rounded-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-4 h-4 text-[#22c55e]" />
                    <span className="text-sm font-semibold text-[#1e3a5f]">{t('inv_verifactu')}</span>
                  </div>
                  <p className="text-xs text-slate-500 font-mono break-all">Hash: {showDetail.verifactu_hash}</p>
                  <p className="text-xs text-slate-400 font-mono break-all mt-1">Prev: {showDetail.previous_hash}</p>
                </div>
                {showDetail.notes && <p className="text-sm text-slate-500 italic">{showDetail.notes}</p>}

                {/* Email Sent Status */}
                {showDetail.email_sent_at && (
                  <div className="flex items-center gap-2 text-xs text-green-600 bg-green-50 p-2 rounded-sm">
                    <Mail className="w-3.5 h-3.5" />
                    <span>{t('inv_email_sent_to')} {showDetail.email_sent_to} ({new Date(showDetail.email_sent_at).toLocaleDateString()})</span>
                  </div>
                )}

                {/* Reminder Status */}
                {showDetail.reminder_count > 0 && (
                  <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 p-2 rounded-sm" data-testid="reminder-status">
                    <span>{t('inv_reminders_sent')}: {showDetail.reminder_count}</span>
                    {showDetail.last_reminder_at && (
                      <span className="text-amber-500">({new Date(showDetail.last_reminder_at).toLocaleDateString()})</span>
                    )}
                  </div>
                )}

                {/* Send Email Section */}
                <div className="border border-slate-200 p-3 rounded-sm">
                  <label className="block text-xs font-semibold text-[#1e3a5f] mb-2 flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5" /> {t('inv_send_email')}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={emailInput || showDetail.customer?.email || ''}
                      onChange={e => setEmailInput(e.target.value)}
                      placeholder={t('inv_customer_email')}
                      className="flex-1 px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none"
                      data-testid="invoice-email-input"
                    />
                    <button
                      onClick={() => handleSendEmail(showDetail.id)}
                      disabled={sendingEmail}
                      className="vf-btn-primary flex items-center gap-1.5 px-4 py-2 text-sm disabled:opacity-50"
                      data-testid="send-invoice-email-btn"
                    >
                      {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      {t('inv_send')}
                    </button>
                  </div>
                </div>
              </div>
              <div className="p-4 border-t border-slate-200 flex gap-2 flex-wrap">
                <button onClick={() => handleDownloadPDF(showDetail.id, showDetail.invoice_number)}
                  className="vf-btn-secondary flex items-center gap-2" data-testid="detail-download-pdf">
                  <Download className="w-4 h-4" /> {t('inv_download_pdf')}
                </button>
                {showDetail.public_token && (
                  <button
                    onClick={() => {
                      const url = `${window.location.origin}/invoice/view/${showDetail.public_token}`;
                      navigator.clipboard.writeText(url);
                      alert(t('inv_link_copied'));
                    }}
                    className="vf-btn-secondary flex items-center gap-2"
                    data-testid="copy-public-link"
                  >
                    <Copy className="w-4 h-4" /> {t('inv_copy_link')}
                  </button>
                )}
                {showDetail.public_token && (
                  <button
                    onClick={() => window.open(`/invoice/view/${showDetail.public_token}`, '_blank')}
                    className="vf-btn-secondary flex items-center gap-2"
                    data-testid="view-public-link"
                  >
                    <ExternalLink className="w-4 h-4" /> {t('inv_view_online')}
                  </button>
                )}
                {showDetail.status === 'issued' && (
                  <button onClick={() => handleMarkPaid(showDetail.id)}
                    className="vf-btn-primary flex items-center gap-2" data-testid="mark-paid-btn">
                    <CheckCircle className="w-4 h-4" /> {t('inv_mark_paid')}
                  </button>
                )}
                {showDetail.status !== 'cancelled' && showDetail.status !== 'paid' && (
                  <button onClick={() => handleCancel(showDetail.id)}
                    className="bg-red-600 text-white px-4 py-2 text-sm font-medium hover:bg-red-700 flex items-center gap-2" data-testid="cancel-invoice-btn">
                    <Ban className="w-4 h-4" /> {t('inv_cancel_invoice')}
                  </button>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </ShopLayout>
  );
};

export default ShopInvoicesPage;
